// page 57, 58, 59 https://xd.adobe.com/view/ef189247-c8fd-4c31-b643-d4151912ea88-ba4b/screen/e3e5f7bf-e88b-4d3d-b07b-741ff9c0a215/specs/

import { Checkbox, Form, Spin, Table } from "antd";
import Column from "antd/es/table/Column";
import ColumnGroup from "antd/es/table/ColumnGroup";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import Styles from "./styled";
import { useState, useEffect } from "react";
import useService from "hook/useService";
import axiosClient from "api/axiosClient";
import { ButtonOutline, ButtonSolid } from "component/button";
import { useWatch } from "antd/es/form/Form";
import { toast } from "react-toastify";
import { fillDateArray } from "./function";
import { IPatient } from "constant/interfaces";
import ROUTES from "constant/routes";
import PopoverChangeAmount from "./PopoverChangeAmount";
import Decimal from "decimal.js";
import { get, isNil, isNumber } from "lodash";

const NO_LIMIT = "管理しない";

export type TUsage = "equipment" | "service" | "potoro";
interface IPopupState {
  rowIndex?: number | undefined;
  colIndex?: number | undefined;
  change?: number;
}
export default function UsageHistory() {
  const service = useService();
  const navigate = useNavigate();
  const [patientData, setPatientData] = useState<IPatient>();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [isAllowUpdate, setIsAllowUpdate] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const equipment_services = useWatch("equipment_services", form);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const month = dayjs(searchParams.get("month") || Date.now());
  const patient_id = searchParams.get("patient_id");
  const type: TUsage = (() => {
    const type = searchParams.get("type") as TUsage;
    if (["equipment", "service", "potoro"].includes(type)) return type;
    return "equipment";
  })();
  const renderTitle = () => {
    let jpTypeText = "";
    switch (type) {
      case "equipment":
        jpTypeText = "備品利用";
        break;
      case "service":
        jpTypeText = "サービス利用";
        break;
      case "potoro":
        jpTypeText = "ぽとろ利用";
        break;
      default:
        jpTypeText = "備品利用";
        break;
    }
    return (
      jpTypeText +
      month.format(" YYYY/MM ") +
      (patientData?.family_name || "") +
      " 様"
    );
  };
  const fetchInitialData = async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosClient.get(service.EQUIPMENT_SERVICES, {
        params: {
          year_month: month.format("YYYY/MM"),
          category: type,
          patient_id: patient_id,
        },
      });
      form.setFieldValue(
        "equipment_services",
        data.serialized_equipment_services.map((i: any) => ({
          ...i,
          unit_price: i.equipment_service_payments?.unit_price || i.unit_price,
          equipment_service_usages:
            i.equipment_service_payments &&
            fillDateArray(
              i.equipment_service_payments?.equipment_service_usages,
              month
            ),
        }))
      );
      form.setFieldValue("registered", data.registered);
      setPatientData(data.patient);
      setIsAllowUpdate(!data.registered);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  const handleCancelRegistered = async () => {
    if (!patient_id) return;
    try {
      setCanceling(true);
      const res = await axiosClient.post(service.CANCEL_PAYMENT, {
        patient_id,
        year_month: month.format("YYYY/MM"),
        category: type,
      });
      res?.data?.success && toast.success(res.data?.success);
      fetchInitialData();
    } catch (e) {
    } finally {
      setCanceling(false);
    }
  };

  const [submitting, setSubmitting] = useState(false);

  const getValueActually = (change?: number, quantity?: number) => {
    if (change || isNumber(change)) return change;
    else return quantity || 0;
  };

  const handleSubmit = async (formData: any) => {
    try {
      setSubmitting(true);
      const res = await axiosClient.post(service.EQUIPMENT_SERVICE_PAYMENTS, {
        patient_id,
        registered: formData.registered,
        year_month: month.format("YYYY/MM"),
        category: type,
        equipment_service_payments: equipment_services
          .filter(
            (i: any) =>
              i.equipment_service_usages?.filter(
                (subItem: any) => !isNil(subItem?.change)
              )?.length
          )
          .map((i: any) => ({
            equipment_service_id: i.id,
            unit_price: i.unit_price,
            equipment_service_usages: i.equipment_service_usages?.filter(
              (subItem: any) => !isNil(subItem?.change)
            ),
          })),
      });
      toast.success(res.data.success);
      navigate(
        `/${ROUTES.ADMIN}/${ROUTES.PAYMENT}?month=${month.format("YYYY/MM")}`
      );
    } catch (error) {
      toast.error((error as string).toString());
    } finally {
      setSubmitting(false);
    }
  };

  const getRemainingStock = (rowIndex: number, record: any) => {
    if (record.stock_quantity === NO_LIMIT) return NO_LIMIT;
    const totalChange = equipment_services[rowIndex].equipment_service_usages
      ?.reduce((c: Decimal, n: any) => {
        const quantity = isNil(n?.change) ? 0 : n?.quantity || 0;
        const change = n?.change || 0;
        return c.add(new Decimal(change).minus(quantity).toNumber());
      }, new Decimal(0))
      .toNumber();

    const remainingStock = new Decimal(record?.stock_quantity || 0)
      .minus(totalChange || 0)
      .toNumber();
    return remainingStock;
  };
  useEffect(() => {
    fetchInitialData();
  }, []);

  return (
    <Spin spinning={isLoading}>
      <Form form={form} onFinish={handleSubmit}>
        <Styles>
          <div className="page-title">{renderTitle()}</div>
          <div className="page-container">
            <Form.List name="equipment_services">
              {(field) => (
                <Table
                  rowKey={(row) => row.id}
                  dataSource={equipment_services}
                  bordered
                  scroll={{ x: true }}
                  pagination={false}
                >
                  <Column
                    title="項目名"
                    dataIndex="service_name"
                    className="font-[600]"
                  />
                  {[...Array(month.daysInMonth())].map((item, index) => (
                    <ColumnGroup
                      className="col-date"
                      title={index + 1}
                      align="center"
                      key={index}
                    >
                      <Column
                        className="col-date"
                        align="center"
                        title={month
                          .locale("ja")
                          .date(index + 1)
                          .format("ddd")}
                        render={(value, record: any, rowIndex) => {
                          const cellValue = getValueActually(
                            get(
                              equipment_services,
                              `[${rowIndex}].equipment_service_usages[${index}].change`
                            ),
                            get(
                              equipment_services,
                              `[${rowIndex}].equipment_service_usages[${index}].quantity`
                            )
                          );
                          const remainingStock = getRemainingStock(
                            rowIndex,
                            record
                          );
                          const available = remainingStock === NO_LIMIT ? Infinity :  new Decimal(remainingStock)
                            .add(cellValue)
                            .toNumber();

                          return (
                            <PopoverChangeAmount
                              type={type}
                              equipment_services={equipment_services}
                              record={record}
                              isAllowUpdate={isAllowUpdate}
                              cellValue={cellValue}
                              index={index}
                              rowIndex={rowIndex}
                              available={
                                available
                              }
                            />
                          );
                        }}
                      />
                    </ColumnGroup>
                  ))}
                  <Column align="center" title="単価" dataIndex="unit_price" />
                  <Column
                    align="center"
                    title="利用数"
                    render={(value, record, index) =>
                      equipment_services[index].equipment_service_usages
                        ?.reduce((c: any, n: any) => {
                          const numberAdd = getValueActually(
                            n?.change,
                            n?.quantity
                          );
                          return c.add(numberAdd);
                        }, new Decimal(0))
                        .toNumber() || ""
                    }
                  />
                  <Column
                    align="center"
                    title="在庫数"
                    render={(value, record: any, rowIndex) => {
                      const cellvalue = getRemainingStock(rowIndex, record);
                      return (
                        <div
                          style={{
                            color:
                              cellvalue === NO_LIMIT
                                ? ""
                                : cellvalue >= 0
                                ? ""
                                : "red",
                          }}
                        >
                          {cellvalue}
                        </div>
                      );
                    }}
                  />
                  <Column
                    align="center"
                    title="合計"
                    render={(value, record: any, index) =>
                      equipment_services[index].equipment_service_usages
                        ?.reduce((c: Decimal, n: any) => {
                          const numberAdd = getValueActually(
                            n?.change,
                            n?.quantity
                          );
                          return c.add(numberAdd);
                        }, new Decimal(0))
                        ?.times(record?.unit_price || 0)
                        .toNumber() || 0
                    }
                  />
                </Table>
              )}
            </Form.List>
            <div className="text-right mt-10">
              <Form.Item
                hidden={!isAllowUpdate}
                noStyle
                name="registered"
                valuePropName="checked"
                initialValue={false}
              >
                <Checkbox>「登録済」にする</Checkbox>
              </Form.Item>
            </div>
            <div className="flex justify-end gap-3 mt-3">
              <ButtonSolid
                hidden={isAllowUpdate}
                w={130}
                onClick={handleCancelRegistered}
                isLoading={canceling}
              >
                登録解除
              </ButtonSolid>
              <ButtonOutline onClick={() => navigate(-1)}>
                キャンセル
              </ButtonOutline>
              <ButtonSolid
                hidden={!isAllowUpdate}
                w={137}
                onClick={form.submit}
                isLoading={submitting}
              >
                保存
              </ButtonSolid>
            </div>
          </div>
        </Styles>
      </Form>
    </Spin>
  );
}
